import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import { ICONE_APP } from '@/lib/app';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import Campo from '@/components/ui/Campo';
import CampoArea from '@/components/base/CampoArea';
import StatCard from '@/components/ui/StatCard';
import ProgressBar from '@/components/ui/ProgressBar';
import Modal from '@/components/ui/Modal';
import type { EventoDestino, FiltrosLeads, OpcoesLeads, PreviaAviso } from '../types';
import {
  descreverGrupo,
  formatarDataBR,
  normalizarOpcoes,
  normalizarPrevia,
  opcoesVazias,
} from '../utils';
import FiltrosPublico from './FiltrosPublico';

const TAM_TITULO = 60;
const TAM_CORPO = 240;

function pluralPessoas(n: number): string {
  return `${n} ${n === 1 ? 'pessoa' : 'pessoas'}`;
}

function pluralAparelhos(n: number): string {
  return `${n} ${n === 1 ? 'aparelho' : 'aparelhos'}`;
}

function filtrosRpc(f: FiltrosLeads): Record<string, unknown> {
  return { eventos: f.eventos, cidades: f.cidades, origens: f.origens };
}

// ---------------------------------------------------------------------------
// Aba "Avisos no celular" do painel da plataforma (só admin). Escolhe o público,
// mostra quantas pessoas/aparelhos recebem e envia o aviso geral por push.
// Quem envia é o banco (`enviar_aviso_geral`); aqui só montamos e disparamos.
// ---------------------------------------------------------------------------
export default function AvisosGerais() {
  const [previa, setPrevia] = useState<PreviaAviso | null>(null);
  const [global, setGlobal] = useState<PreviaAviso | null>(null);
  const [opcoes, setOpcoes] = useState<OpcoesLeads>(() => opcoesVazias());
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [eventos, setEventos] = useState<EventoDestino[]>([]);

  const [grupo, setGrupo] = useState<FiltrosLeads>({ eventos: [], cidades: [], origens: [] });

  const [titulo, setTitulo] = useState('');
  const [corpo, setCorpo] = useState('');
  const [destinoSlug, setDestinoSlug] = useState('');

  const [confirmar, setConfirmar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);

  const inicioRef = useRef(true);

  const carregarPrevia = useCallback(async (f: FiltrosLeads) => {
    try {
      const { data, error } = await supabase.rpc('aviso_geral_previa', { p_filtros: filtrosRpc(f) });
      if (error) throw error;
      const p = normalizarPrevia(data);
      if (!p) throw new Error('Não foi possível ler os números agora.');
      setPrevia(p);
      setErro(null);
    } catch (e) {
      setErro(mensagemAmigavel(e));
    }
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [op, pv] = await Promise.all([
        supabase.rpc('leads_opcoes'),
        supabase.rpc('aviso_geral_previa', { p_filtros: {} }),
      ]);
      if (op.error) throw op.error;
      if (pv.error) throw pv.error;
      setOpcoes(normalizarOpcoes(op.data));
      const p = normalizarPrevia(pv.data);
      if (!p) throw new Error('Não foi possível ler os números agora.');
      setGlobal(p);
      setPrevia(p);
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Atualiza a prévia quando o grupo muda (espera 400 ms para não ir a cada toque).
  useEffect(() => {
    if (inicioRef.current) {
      inicioRef.current = false;
      return;
    }
    const t = window.setTimeout(() => {
      void carregarPrevia(grupo);
    }, 400);
    return () => window.clearTimeout(t);
  }, [grupo, carregarPrevia]);

  // Eventos publicados para o seletor "Abrir ao tocar".
  useEffect(() => {
    let ativo = true;
    (async () => {
      const { data } = await supabase
        .from('eventos')
        .select('id, nome, slug')
        .eq('status', 'publicado')
        .order('inicio_em', { ascending: false })
        .limit(200);
      if (ativo) setEventos((data as EventoDestino[]) || []);
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const pessoas = previa?.pessoas ?? 0;
  const aparelhos = previa?.aparelhos ?? 0;
  const leadsNoGrupo = previa?.leads_no_grupo ?? 0;
  const totalPessoas = global?.pessoas ?? 0;
  const totalAparelhos = global?.aparelhos ?? 0;
  const contas = global?.contas ?? 0;
  const ativas30d = global?.ativas_30d ?? 0;
  const enviados24h = global?.enviados_24h ?? 0;
  const limite = global?.limite_dia ?? 3;
  const app = global?.app;
  const noLimite = enviados24h >= limite;
  const pct = contas > 0 ? Math.round((totalPessoas / contas) * 100) : 0;

  const descricaoGrupo = descreverGrupo(grupo, opcoes);
  const url = destinoSlug ? `/e/${destinoSlug}` : '/';
  const tituloOk = titulo.trim().length >= 3 && titulo.length <= TAM_TITULO;
  const corpoOk = corpo.trim().length >= 3 && corpo.length <= TAM_CORPO;
  const podeEnviar = !noLimite && tituloOk && corpoOk && pessoas > 0;

  async function enviar() {
    setEnviando(true);
    setErroEnvio(null);
    try {
      const { data, error } = await supabase.rpc('enviar_aviso_geral', {
        p_titulo: titulo.trim(),
        p_corpo: corpo.trim(),
        p_url: url,
        p_filtros: filtrosRpc(grupo),
      });
      if (error) throw error;
      const r = (data ?? {}) as { ok?: boolean; pessoas?: number };
      const n = Number(r.pessoas) || 0;
      setConfirmar(false);
      setTitulo('');
      setCorpo('');
      mostrarToast(`Enviado para ${pluralPessoas(n)}.`, 5000, 'sucesso');
      await carregarPrevia(grupo);
    } catch (e) {
      setErroEnvio(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-[18px] bg-background-200/70" />
          ))}
        </div>
        <div className="h-24 animate-pulse rounded-[18px] bg-background-200/70" />
        <div className="h-44 animate-pulse rounded-[18px] bg-background-200/70" />
      </div>
    );
  }

  if (erro && !global) {
    return (
      <Cartao className="p-6 text-center">
        <p className="text-base font-semibold text-foreground-950">Não foi possível carregar</p>
        <p className="mt-1 text-sm text-foreground-600">{erro}</p>
        <div className="mt-5 flex justify-center">
          <Botao variante="primario" icone="ri-refresh-line" onClick={() => void carregar()}>
            Tentar de novo
          </Botao>
        </div>
      </Cartao>
    );
  }

  return (
    <div className="space-y-6">
      {/* Números gerais */}
      <section>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
          <StatCard
            rotulo="Com avisos ligados"
            valor={totalPessoas}
            nota={pluralAparelhos(totalAparelhos)}
            destaque
            icone="ri-notification-3-line"
          />
          <StatCard rotulo="Contas" valor={contas} icone="ri-user-3-line" />
          <StatCard rotulo="Entraram em 30 dias" valor={ativas30d} icone="ri-calendar-check-line" />
        </div>
        <div className="mt-4">
          <ProgressBar valor={pct} cor="primary" />
          <p className="mt-2 text-sm text-foreground-600">{pct}% das contas recebem avisos</p>
        </div>
      </section>

      {/* App instalado */}
      {app && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-foreground-500">
            App instalado
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
            <StatCard
              rotulo="Instalados"
              valor={app.instalados}
              nota={`Android ${app.instalados_android} · iPhone ${app.instalados_ios}`}
              icone="ri-smartphone-line"
            />
            <StatCard
              rotulo="Abriram em 7 dias"
              valor={app.abriram_7d}
              icone="ri-calendar-check-line"
            />
            <StatCard
              rotulo="Aparelhos vistos"
              valor={app.aparelhos_vistos}
              icone="ri-device-line"
            />
          </div>
          <p className="mt-2 text-xs text-foreground-500">
            {app.contando_desde
              ? `contando desde ${formatarDataBR(app.contando_desde)}`
              : 'começa a contar depois de publicar'}
          </p>
        </section>
      )}

      {/* Para quem? */}
      <section>
        <Cartao className="p-4 md:p-5">
          <FiltrosPublico opcoes={opcoes} grupo={grupo} onChange={setGrupo} />
        </Cartao>
      </section>

      {/* Chega em X */}
      <section>
        <div className="rounded-[18px] bg-primary-50 p-4 md:p-5">
          <p className="text-2xl font-extrabold leading-tight text-primary-800">
            Chega em {pluralPessoas(pessoas)} ({pluralAparelhos(aparelhos)})
          </p>
          <p className="mt-1.5 text-sm text-foreground-600">
            {leadsNoGrupo} {leadsNoGrupo === 1 ? 'pessoa' : 'pessoas'} nesse grupo — só quem ligou
            os avisos recebe
          </p>
        </div>
      </section>

      {/* Formulário */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-foreground-500">
          Novo aviso
        </h2>
        <Cartao className="p-4 md:p-5">
          <div className="space-y-4">
            <div>
              <Campo
                rotulo="Título"
                value={titulo}
                maxLength={TAM_TITULO}
                onChange={setTitulo}
                placeholder="Ex.: Fichas abertas no Acampamento"
              />
              <p className="mt-1 text-right text-xs text-foreground-500">
                {titulo.length}/{TAM_TITULO}
              </p>
            </div>

            <CampoArea
              id="aviso-corpo"
              name="corpo"
              label="Mensagem"
              value={corpo}
              onChange={setCorpo}
              maxLength={TAM_CORPO}
              simples
              placeholder="Escreva a mensagem do aviso."
            />

            <div>
              <label
                htmlFor="aviso-destino"
                className="mb-1.5 block text-sm font-medium text-foreground-800"
              >
                Abrir ao tocar
              </label>
              <select
                id="aviso-destino"
                value={destinoSlug}
                onChange={(e) => setDestinoSlug(e.target.value)}
                className="h-12 w-full rounded-xl border border-secondary-300 bg-white px-3.5 text-base text-foreground-950 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="">Início (tela do app)</option>
                {eventos.map((ev) => (
                  <option key={ev.id} value={ev.slug}>
                    {ev.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Cartao>
      </section>

      {/* Prévia */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-foreground-500">
          Como chega no celular
        </h2>
        <div className="mx-auto w-full max-w-sm rounded-[22px] bg-background-100 p-3">
          <div className="flex items-start gap-3 rounded-[16px] bg-background-50 p-3 shadow-pcard">
            <img src={ICONE_APP} alt="" className="h-10 w-10 shrink-0 rounded-xl object-cover" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold text-foreground-950">Se Liga</span>
                <span className="shrink-0 text-[11px] text-foreground-400">agora</span>
              </div>
              <p className="mt-0.5 line-clamp-1 text-[13px] font-bold text-foreground-950">
                {titulo.trim() || 'Título do aviso'}
              </p>
              <p className="line-clamp-2 text-[13px] text-foreground-600">
                {corpo.trim() || 'A mensagem aparece aqui, direto na tela do celular.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Enviar */}
      <section>
        <Botao
          cheio
          variante="primario"
          icone="ri-send-plane-2-line"
          disabled={!podeEnviar}
          onClick={() => {
            setErroEnvio(null);
            setConfirmar(true);
          }}
        >
          Enviar para {pluralPessoas(pessoas)}
        </Botao>
        <p className="mt-3 text-center text-sm text-foreground-500">
          Hoje: {enviados24h} de {limite} avisos gerais
        </p>
        {noLimite && (
          <p className="mt-1 text-center text-sm text-warning-700">
            Limite de hoje atingido. Amanhã você pode enviar de novo.
          </p>
        )}
        {!noLimite && pessoas === 0 && (
          <p className="mt-1 text-center text-sm text-foreground-500">
            Ninguém desse grupo está com os avisos ligados ainda.
          </p>
        )}
        {erroEnvio && (
          <p className="mt-3 flex items-start justify-center gap-1.5 text-sm text-danger-600" role="alert">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
              <i className="ri-error-warning-line" />
            </span>
            <span className="min-w-0">{erroEnvio}</span>
          </p>
        )}
      </section>

      <Modal
        aberto={confirmar}
        titulo="Confirmar aviso"
        aoFechar={() => {
          if (!enviando) setConfirmar(false);
        }}
        rodape={
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Botao variante="secundario" onClick={() => setConfirmar(false)} disabled={enviando}>
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              icone="ri-send-plane-2-line"
              carregando={enviando}
              onClick={() => void enviar()}
            >
              Enviar agora
            </Botao>
          </div>
        }
      >
        <p className="text-sm text-foreground-700">
          Vai chegar agora no celular de{' '}
          <strong className="font-semibold text-foreground-950">{pluralPessoas(pessoas)}</strong>.
        </p>
        <p className="mt-2 text-sm text-foreground-600">
          Público: <span className="font-semibold text-foreground-900">{descricaoGrupo}</span>
        </p>
        {erroEnvio && (
          <p className="mt-3 text-sm text-danger-600" role="alert">
            {erroEnvio}
          </p>
        )}
      </Modal>
    </div>
  );
}